import React, { useState, useEffect } from 'react';
import { Calendar, Check, Award, User, LogOut, Menu, X, ChevronRight, Star, Shield, Clock, Phone, Mail, MapPin, MessageCircle, Send } from 'lucide-react';
import { supabase } from './supabaseClient';

// Utility function to generate unique IDs
const generateId = () => '_' + Math.random().toString(36).substr(2, 9);

const SlaterBrosLanding = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [bookingDetails, setBookingDetails] = useState('');
  const [userBookings, setUserBookings] = useState([]);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);
  
  // Guest booking state
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  // Payment state
  const [showPaymentTab, setShowPaymentTab] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentEmail, setPaymentEmail] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Auth form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  // Payment form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');

  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [chatStarted, setChatStarted] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewName, setReviewName] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [reviewWork, setReviewWork] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminReplyText, setAdminReplyText] = useState('');
  const [replyingToReview, setReplyingToReview] = useState(null);


  const services = [
    { id: 1, name: 'Lawn & Garden Maintenance', icon: '🌿', price: 45 },
    { id: 2, name: 'Gutter Cleaning', icon: '🏠', price: 65 },
    { id: 3, name: 'Painting & Decorating', icon: '🎨', price: 95 },
    { id: 4, name: 'House Removals', icon: '📦', price: 150 },
    { id: 5, name: 'Rubbish Clearance', icon: '🚛', price: 75 },
    { id: 6, name: 'Garage/Loft Organization', icon: '🏚️', price: 85 },
    { id: 7, name: 'Flatpack Assembly', icon: '🔧', price: 55 },
    { id: 8, name: 'Blockwork', icon: '🧱', price: 120 },
    { id: 9, name: 'Fencing', icon: '🪵', price: 110 },
    { id: 10, name: 'General Handyman', icon: '🛠️', price: 50 },
    { id: 11, name: 'AI Automation', icon: '🤖', price: 0 },
    { id: 12, name: 'Custom Built Software Packages', icon: '💻', price: 0 },
    { id: 13, name: 'Car Wash Services', icon: '🚗', price: 0 },
    { id: 14, name: 'Household & Commercial Cleaning', icon: '🧹', price: 0 },
    { id: 15, name: 'Property Maintenance', icon: '🏗️', price: 0 },
    { id: 16, name: 'Delivery Service', icon: '🚚', price: 0 },
    { id: 17, name: 'Other Job', icon: '📋', price: 0 }
  ];

  // Load user data on mount
  useEffect(() => {
    loadUserData();
    loadReviews();
  }, []);

  // Load user data when user logs in
  useEffect(() => {
    if (currentUser) {
      loadUserBookings();
      loadLoyaltyPoints();
    }
  }, [currentUser]);

  const loadUserData = () => {
    try {
      const userData = localStorage.getItem('slater_current_user');
      if (userData) {
        setCurrentUser(JSON.parse(userData));
      }
    } catch (error) {
      console.log('No current user');
    }
  };

  const loadUserBookings = () => {
    if (!currentUser) return;
    try {
      const bookingsData = localStorage.getItem(`slater_bookings_${currentUser.id}`);
      if (bookingsData) {
        setUserBookings(JSON.parse(bookingsData));
      }
    } catch (error) {
      setUserBookings([]);
    }
  };

  const loadLoyaltyPoints = () => {
    if (!currentUser) return;
    try {
      const points = localStorage.getItem(`slater_loyalty_${currentUser.id}`);
      if (points) {
        setLoyaltyPoints(parseInt(points));
      } else {
        setLoyaltyPoints(0);
      }
    } catch (error) {
      setLoyaltyPoints(0);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      // Check if user already exists in localStorage
      const userKey = `slater_user_${email}`;
      const existingUser = localStorage.getItem(userKey);
      
      if (existingUser) {
        alert('An account with this email already exists. Please login.');
        return;
      }

      const newUser = {
        id: generateId(),
        email,
        password,
        name,
        phone,
        address,
        createdAt: new Date().toISOString()
      };

      // Save to Supabase database
      try {
        const { data, error } = await supabase
          .from('users')
          .insert([
            {
              id: newUser.id,
              email: newUser.email,
              password: newUser.password, // Note: In production, hash this!
              name: newUser.name,
              phone: newUser.phone || null,
              address: newUser.address || null
            }
          ]);

        if (error) {
          console.error('Supabase error creating user:', error);
          // Continue with localStorage if Supabase fails
        } else {
          console.log('User saved to database successfully!');
        }

        // Initialize loyalty points in database
        await supabase
          .from('loyalty_points')
          .insert([
            {
              user_id: newUser.id,
              points: 0
            }
          ]);
      } catch (dbError) {
        console.error('Error saving user to database:', dbError.message);
        // Continue with localStorage even if database fails
      }

      // Save to localStorage as backup
      localStorage.setItem(userKey, JSON.stringify(newUser));
      localStorage.setItem('slater_current_user', JSON.stringify(newUser));
      localStorage.setItem(`slater_loyalty_${newUser.id}`, '0');

      // Send email notification to Slater Bros about new user
      try {
        const emailMessage = `
NEW USER REGISTRATION
=====================

A new customer has registered on your website!

CUSTOMER DETAILS:
Name: ${newUser.name}
Email: ${newUser.email}
Phone: ${newUser.phone || 'Not provided'}
Address: ${newUser.address || 'Not provided'}

NEXT STEPS:
- Send a welcome email to ${newUser.email}
- Introduce your services
- Offer assistance with their first booking

Registration Date: ${new Date().toLocaleString('en-GB')}
User ID: ${newUser.id}
        `;

        const formData = new FormData();
        formData.append('email', 'slaterbrosprojects@gmail.com');
        formData.append('subject', `🎉 New Customer Registration - ${newUser.name}`);
        formData.append('message', emailMessage);
        formData.append('_captcha', 'false');

        await fetch('https://formsubmit.co/slaterbrosprojects@gmail.com', {
          method: 'POST',
          body: formData
        });

        console.log('New user notification email sent');
      } catch (emailError) {
        console.error('Error sending registration email:', emailError);
        // Continue even if email fails
      }
      
      setCurrentUser(newUser);
      setShowAuthModal(false);
      resetAuthForm();
      alert('Account created successfully! Welcome to Slater Bros!');
    } catch (error) {
      console.error('Signup error:', error);
      alert('Error creating account. Please try again.');
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    try {
      const userKey = `slater_user_${email}`;
      const userData = localStorage.getItem(userKey);
      
      if (userData) {
        const user = JSON.parse(userData);
        if (user.password === password) {
          localStorage.setItem('slater_current_user', JSON.stringify(user));
          setCurrentUser(user);
          setShowAuthModal(false);
          resetAuthForm();
          alert('Welcome back!');
        } else {
          alert('Incorrect password. Please try again.');
        }
      } else {
        alert('No account found with this email.');
      }
    } catch (error) {
      alert('No account found with this email.');
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('slater_current_user');
      setCurrentUser(null);
      setUserBookings([]);
      setLoyaltyPoints(0);
    } catch (error) {
      console.log('Error logging out');
    }
  };

  const resetAuthForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setPhone('');
    setAddress('');
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    // Determine if this is a guest or logged-in user
    const isGuest = !currentUser;
    const customerName = isGuest ? guestName : currentUser.name;
    const customerEmail = isGuest ? guestEmail : currentUser.email;
    const customerPhone = isGuest ? guestPhone : currentUser.phone;
    const customerId = isGuest ? 'guest' : currentUser.id;
    const hasDiscount = !isGuest && loyaltyPoints >= 10;

    try {
      const booking = {
        id: generateId(),
        userId: customerId,
        service: selectedService,
        date: bookingDate,
        time: bookingTime,
        details: bookingDetails,
        status: 'confirmed',
        createdAt: new Date().toISOString(),
        hasLoyaltyDiscount: hasDiscount,
        isGuest: isGuest
      };

      // Save to Supabase database
      try {
        const { data, error } = await supabase
          .from('bookings')
          .insert([
            {
              customer_name: customerName,
              customer_email: customerEmail,
              customer_phone: customerPhone || null,
              service: selectedService,
              booking_date: bookingDate,
              booking_time: bookingTime,
              details: bookingDetails || null,
              has_loyalty_discount: hasDiscount,
              status: 'confirmed'
            }
          ]);
        
        if (error) {
          console.error('Supabase error:', error);
        } else {
          console.log('Booking saved to database successfully!');
        }
      } catch (dbError) {
        console.error('Error saving to database:', dbError.message);
        // Continue even if database fails
      }

      // Save to localStorage only for logged-in users
      if (!isGuest) {
        const newBookings = [...userBookings, booking];
        localStorage.setItem(`slater_bookings_${currentUser.id}`, JSON.stringify(newBookings));
        setUserBookings(newBookings);
      }

      // Send email notification to Slater Bros
      try {
        const emailMessage = `
NEW ${isGuest ? 'GUEST ' : ''}BOOKING REQUEST RECEIVED
============================

CUSTOMER DETAILS:
Name: ${customerName}
Email: ${customerEmail}
Phone: ${customerPhone || 'Not provided'}
${isGuest ? 'Status: Guest User (No Account)' : 'Status: Registered User'}

BOOKING DETAILS:
Service: ${selectedService}
Date: ${new Date(bookingDate).toLocaleDateString('en-GB')}
Time: ${bookingTime}
Additional Details: ${bookingDetails || 'None provided'}

${hasDiscount ? '🎉 LOYALTY DISCOUNT: Customer has 10+ points - 20% discount applies!' : ''}

NEXT STEPS:
- Contact customer at ${customerEmail} or ${customerPhone || 'email only'}
- Provide quote for ${selectedService}
- Confirm booking details

Booking ID: ${booking.id}
Submitted: ${new Date().toLocaleString('en-GB')}
        `;

        const formData = new FormData();
        formData.append('email', 'slaterbrosprojects@gmail.com');
        formData.append('subject', `🔔 New ${isGuest ? 'Guest ' : ''}Booking: ${selectedService} - ${customerName}`);
        formData.append('message', emailMessage);
        formData.append('_captcha', 'false');

        await fetch('https://formsubmit.co/slaterbrosprojects@gmail.com', {
          method: 'POST',
          body: formData
        });
      } catch (emailError) {
        console.error('Error sending email:', emailError);
        // Continue even if email fails
      }

      // Handle loyalty points for logged-in users only
      if (!isGuest) {
        const newPoints = hasDiscount ? 0 : loyaltyPoints + 1;
        localStorage.setItem(`slater_loyalty_${currentUser.id}`, newPoints.toString());
        setLoyaltyPoints(newPoints);

        // Update loyalty points in database
        try {
          await supabase
            .from('loyalty_points')
            .upsert(
              {
                user_id: currentUser.id,
                points: newPoints
              },
              { onConflict: 'user_id' }
            );
        } catch (dbError) {
          console.error('Error updating loyalty points:', dbError.message);
        }
      }

      // Reset form
      setShowBookingModal(false);
      setSelectedService('');
      setBookingDate('');
      setBookingTime('');
      setBookingDetails('');
      setGuestName('');
      setGuestEmail('');
      setGuestPhone('');
      
      if (isGuest) {
        alert('Quote request submitted! We will contact you shortly at ' + customerEmail + ' to discuss your requirements and provide a quote.');
      } else if (hasDiscount) {
        alert('Booking confirmed! Your 20% loyalty discount has been applied. We will contact you shortly to confirm details and provide your discounted quote. Your loyalty points have been reset to 0.');
      } else {
        alert('Booking confirmed! We will contact you shortly to confirm details and provide a quote. You earned 1 loyalty point!');
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert('Error creating booking. Please try again.');
    }
  };

  const handlePayment = (e) => {
    e.preventDefault();
    setPaymentProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      try {
        // Complete the booking after successful payment
        const completedBooking = {
          ...pendingBooking,
          status: 'confirmed',
          paymentId: generateId(),
          paidAt: new Date().toISOString()
        };

        // Add booking
        const newBookings = [...userBookings, completedBooking];
        localStorage.setItem(`slater_bookings_${currentUser.id}`, JSON.stringify(newBookings));
        setUserBookings(newBookings);

        // Update loyalty points
        const newPoints = pendingBooking.discount > 0 ? 0 : loyaltyPoints + 1;
        localStorage.setItem(`slater_loyalty_${currentUser.id}`, newPoints.toString());
        setLoyaltyPoints(newPoints);

        // Reset form
        setShowPaymentModal(false);
        setPaymentProcessing(false);
        setPendingBooking(null);
        setSelectedService('');
        setBookingDate('');
        setBookingTime('');
        setBookingDetails('');
        setCardNumber('');
        setCardExpiry('');
        setCardCvc('');
        setCardName('');
        
        alert(`Payment successful! Booking confirmed. ${pendingBooking.discount > 0 ? 'Your loyalty discount was applied! 🎉' : 'You earned 1 loyalty point!'}`);
      } catch (error) {
        setPaymentProcessing(false);
        alert('Error processing payment. Please try again.');
      }
    }, 2000); // Simulate 2 second payment processing
  };

  // Chat Functions
  const startChat = () => {
    if (!customerName.trim() || !customerEmail.trim()) {
      alert('Please enter your name and email to start chatting');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    setChatStarted(true);
    
    // Bot welcome message
    const welcomeMessage = {
      id: Date.now(),
      text: `Welcome to Slater Bros Projects, ${customerName}! 👋\n\nThank you for reaching out to us. We're here to help with all your service needs.\n\nPlease describe what you need assistance with, and a member of our team will respond as soon as possible. Our typical response time is within 3 hours during business hours.\n\nHow can we help you today?`,
      sender: 'bot',
      timestamp: new Date().toLocaleTimeString()
    };

    setChatMessages([welcomeMessage]);
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: chatInput,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString()
    };

    setChatMessages(prev => [...prev, userMessage]);
    const messageText = chatInput;
    setChatInput('');
    setSendingMessage(true);

    // Save to Supabase database
    try {
      await supabase
        .from('chat_messages')
        .insert([
          {
            customer_name: customerName,
            customer_email: customerEmail,
            message: messageText,
            sender: 'user'
          }
        ]);
    } catch (error) {
      console.error('Error saving chat message to database:', error.message);
    }

    // Send email notification to Slater Bros
    try {
      const emailData = {
        to: 'slaterbrosprojects@gmail.com',
        subject: `New Chat Message from ${customerName}`,
        message: `
New chat message received:

From: ${customerName}
Email: ${customerEmail}
Time: ${new Date().toLocaleString()}

Message:
${messageText}

---
Reply to customer at: ${customerEmail}
        `
      };

      // Using a free email service (FormSubmit) - no server needed
      const formData = new FormData();
      formData.append('email', emailData.to);
      formData.append('subject', emailData.subject);
      formData.append('message', emailData.message);
      formData.append('_captcha', 'false');

      await fetch('https://formsubmit.co/slaterbrosprojects@gmail.com', {
        method: 'POST',
        body: formData
      });

      // Bot acknowledgment
      setTimeout(() => {
        const botResponse = {
          id: Date.now() + 1,
          text: `Thank you for your message, ${customerName}! We've received it and will get back to you shortly. Please note that our response time may take up to 3 hours.`,
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString()
        };
        setChatMessages(prev => [...prev, botResponse]);
        setSendingMessage(false);
      }, 1000);

    } catch (error) {
      console.error('Error sending message:', error);
      setSendingMessage(false);
      
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Message received! We will respond to you at ' + customerEmail + ' within 3 hours.',
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // Review Functions
  const loadReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      if (data) {
        // Transform Supabase data to match our component structure
        const transformedReviews = data.map(review => ({
          id: review.id,
          name: review.name,
          date: review.work_date,
          work: review.work_description,
          comments: review.comments,
          rating: review.rating,
          createdAt: review.created_at,
          status: review.status,
          adminReply: review.admin_reply ? {
            text: review.admin_reply,
            timestamp: review.admin_reply_timestamp
          } : null
        }));
        setReviews(transformedReviews);
      }
    } catch (error) {
      console.log('Error loading reviews:', error.message);
      // Fallback to localStorage if Supabase fails
      try {
        const storedReviews = localStorage.getItem('slater_reviews');
        if (storedReviews) {
          setReviews(JSON.parse(storedReviews));
        }
      } catch (localError) {
        console.log('No reviews found');
      }
    }
  };

  const submitReview = async () => {
    if (!reviewName.trim() || !reviewDate || !reviewWork.trim() || !reviewComments.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setSubmittingReview(true);

    try {
      const { data, error } = await supabase
        .from('reviews')
        .insert([
          {
            name: reviewName,
            work_date: reviewDate,
            work_description: reviewWork,
            comments: reviewComments,
            rating: reviewRating,
            status: 'pending'
          }
        ])
        .select();

      if (error) throw error;

      // Reload reviews from database
      await loadReviews();

      // Reset form
      setReviewName('');
      setReviewDate('');
      setReviewWork('');
      setReviewComments('');
      setReviewRating(5);
      setShowReviewForm(false);
      setSubmittingReview(false);

      alert('Thank you for your review! It will be published after admin approval.');
    } catch (error) {
      console.error('Error submitting review:', error.message);
      setSubmittingReview(false);
      alert('Error submitting review. Please try again.');
    }
  };

  const adminLogin = () => {
    // Simple password check - in production, use proper authentication
    if (adminPassword === 'SlaterBros2026!') {
      setIsAdmin(true);
      setShowAdminLogin(false);
      setAdminPassword('');
      alert('Admin access granted');
    } else {
      alert('Incorrect password');
    }
  };

  const approveReview = async (reviewId) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ status: 'approved' })
        .eq('id', reviewId);

      if (error) throw error;

      await loadReviews();
    } catch (error) {
      console.error('Error approving review:', error.message);
      alert('Error approving review. Please try again.');
    }
  };

  const deleteReview = async (reviewId) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        const { error } = await supabase
          .from('reviews')
          .delete()
          .eq('id', reviewId);

        if (error) throw error;

        await loadReviews();
      } catch (error) {
        console.error('Error deleting review:', error.message);
        alert('Error deleting review. Please try again.');
      }
    }
  };

  const submitAdminReply = async (reviewId) => {
    if (!adminReplyText.trim()) {
      alert('Please enter a reply');
      return;
    }

    try {
      const { error } = await supabase
        .from('reviews')
        .update({
          admin_reply: adminReplyText,
          admin_reply_timestamp: new Date().toISOString()
        })
        .eq('id', reviewId);

      if (error) throw error;

      await loadReviews();
      setAdminReplyText('');
      setReplyingToReview(null);
      alert('Reply posted successfully!');
    } catch (error) {
      console.error('Error posting reply:', error.message);
      alert('Error posting reply. Please try again.');
    }
  };

  // Payment Processing Function - Using Stripe Checkout (Secure)
  const handleStripePayment = async (e) => {
    e.preventDefault();

    // Validation
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    if (!paymentEmail || !paymentEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setProcessingPayment(true);

    try {
      const amount = parseFloat(paymentAmount);
      
      // Save pending payment to Supabase
      const paymentData = {
        amount: amount,
        currency: 'GBP',
        customer_email: paymentEmail,
        customer_name: paymentEmail.split('@')[0], // Use email prefix as name
        booking_reference: paymentReference || null,
        status: 'pending',
        payment_method: 'stripe_checkout',
        created_at: new Date().toISOString()
      };

      try {
        await supabase
          .from('payments')
          .insert([paymentData]);
      } catch (dbError) {
        console.error('Error saving payment record:', dbError);
      }

      // Send email notification to Slater Bros
      try {
        const emailMessage = `
💳 NEW PAYMENT REQUEST
======================

A customer has initiated a payment.

PAYMENT DETAILS:
Amount: £${amount.toFixed(2)}
Currency: GBP
Date: ${new Date().toLocaleString('en-GB')}

CUSTOMER DETAILS:
Email: ${paymentEmail}
Booking Reference: ${paymentReference || 'Not provided'}

NEXT STEPS:
1. Log in to your Stripe Dashboard: https://dashboard.stripe.com
2. Create a Payment Link for £${amount.toFixed(2)}
3. Send the payment link to ${paymentEmail}

Alternatively, you can create a payment link here:
https://dashboard.stripe.com/payment-links/create

Customer is waiting for payment instructions.
        `;

        const formData = new FormData();
        formData.append('email', 'slaterbrosprojects@gmail.com');
        formData.append('subject', `💳 Payment Request - £${amount.toFixed(2)} from ${paymentEmail}`);
        formData.append('message', emailMessage);
        formData.append('_captcha', 'false');

        await fetch('https://formsubmit.co/slaterbrosprojects@gmail.com', {
          method: 'POST',
          body: formData
        });
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
      }

      // Reset form
      setPaymentAmount('');
      setPaymentEmail('');
      setPaymentReference('');
      setProcessingPayment(false);
      setShowPaymentTab(false);

      // Show success message with instructions
      alert(`Thank you! Your payment request for £${amount.toFixed(2)} has been received.\n\nWe will send you a secure payment link via email to ${paymentEmail} within 24 hours.\n\nYou'll be able to complete your payment safely through Stripe's secure checkout page.`);
      
    } catch (error) {
      setProcessingPayment(false);
      console.error('Payment request error:', error);
      alert(`Error submitting payment request: ${error.message}. Please try again or contact us directly.`);
    }
  };

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, sans-serif",
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #4a5f7f 100%)',
      color: '#fff',
      scrollBehavior: 'smooth'
    }}>
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        width: '100%',
        background: 'rgba(44, 62, 80, 0.95)',
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '1rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#fff', lineHeight: '1' }}>
              Slater Bros Projects
            </div>
            <div style={{ fontSize: '0.75rem', color: '#f39c12', fontStyle: 'italic' }}>
              Big enough to cope, small enough to care
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              style={{
                display: 'none',
                background: 'none',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                '@media (max-width: 768px)': { display: 'block' }
              }}
            >
              {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div style={{
              display: 'flex',
              gap: '2rem',
              alignItems: 'center',
              '@media (max-width: 768px)': { display: showMobileMenu ? 'flex' : 'none' }
            }}>
              <a href="#services" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.95rem', fontWeight: '500' }}>Services</a>
              <a href="#about" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.95rem', fontWeight: '500' }}>About</a>
              <a href="#reviews" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.95rem', fontWeight: '500' }}>Reviews</a>
              <a href="#contact" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.95rem', fontWeight: '500' }}>Contact</a>
              <button
                onClick={() => setShowPaymentTab(true)}
                style={{
                  background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  cursor: 'pointer'
                }}
              >
                💳 Make a Payment
              </button>
              
              {currentUser ? (
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold'
                  }}>
                    <Award size={16} style={{ display: 'inline', marginRight: '0.3rem' }} />
                    {loyaltyPoints}/10 Points
                  </div>
                  <button
                    onClick={handleLogout}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      color: '#fff',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setShowAuthModal(true);
                    setAuthMode('login');
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                    border: 'none',
                    color: '#fff',
                    padding: '0.6rem 1.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                  }}
                >
                  <User size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        marginTop: '80px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px' }}>
          <h1 style={{
            fontSize: 'clamp(2.5rem, 8vw, 5rem)',
            fontWeight: '800',
            marginBottom: '1.5rem',
            lineHeight: '1.1',
            background: 'linear-gradient(135deg, #fff, #f39c12)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Your Trusted Home Service Experts
          </h1>
          <p style={{
            fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
            marginBottom: '2rem',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6'
          }}>
            From lawn care to house moves, we handle every job with pride and expertise. 
            No job is too small—experience our five-star service today.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                if (currentUser) {
                  setShowBookingModal(true);
                } else {
                  setShowAuthModal(true);
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                border: 'none',
                color: '#fff',
                padding: '1rem 2.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 10px 30px rgba(243, 156, 18, 0.3)',
                transition: 'transform 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Calendar size={20} />
              Quote Now
              <ChevronRight size={20} />
            </button>
            <a href="#services" style={{ textDecoration: 'none' }}>
              <button
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: '#fff',
                  padding: '1rem 2.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
              >
                View Services
              </button>
            </a>
          </div>

          {/* Trust Indicators */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '3rem',
            marginTop: '4rem',
            flexWrap: 'wrap'
          }}>
            {[
              { icon: <Star />, text: '5-Star Service' },
              { icon: <Shield />, text: 'Fully Insured' },
              { icon: <Clock />, text: 'Same-Day Availability on Selected Jobs' }
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#f39c12' }}>
                {item.icon}
                <span style={{ color: '#fff', fontSize: '0.95rem' }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={{
        padding: '6rem 2rem',
        background: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        scrollMarginTop: '80px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '800',
            marginBottom: '3rem',
            textAlign: 'center'
          }}>Our Services</h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {services.map(service => (
              <div
                key={service.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '2rem',
                  transition: 'all 0.3s',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.borderColor = '#f39c12';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onClick={() => {
                  if (currentUser) {
                    setSelectedService(service.name);
                    setShowBookingModal(true);
                  } else {
                    setShowAuthModal(true);
                  }
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{service.icon}</div>
                <h3 style={{ fontSize: '1.3rem', marginBottom: '0.5rem', fontWeight: '700' }}>{service.name}</h3>
              </div>
            ))}
          </div>

          <p style={{
            textAlign: 'center',
            color: '#f39c12',
            fontSize: '1.2rem',
            fontWeight: '600',
            marginTop: '3rem',
            fontStyle: 'italic'
          }}>
            Get in touch for a free quote today!
          </p>
        </div>
      </section>

      {/* Loyalty Program Section */}
      <section style={{
        padding: '6rem 2rem',
        background: 'linear-gradient(135deg, #f39c12, #e67e22)',
        color: '#fff'
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <Award size={60} style={{ marginBottom: '1rem' }} />
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '800',
            marginBottom: '1rem'
          }}>Loyalty Rewards Program</h2>
          <p style={{
            fontSize: '1.2rem',
            marginBottom: '2rem',
            lineHeight: '1.6'
          }}>
            Every job you book earns you 1 reward point. Collect 10 points and receive an exclusive 20% discount on your next service!
          </p>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            borderRadius: '15px',
            padding: '2rem',
            maxWidth: '500px',
            margin: '0 auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '1rem'
            }}>
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: currentUser && loyaltyPoints > i ? '#fff' : 'rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    color: currentUser && loyaltyPoints > i ? '#f39c12' : '#fff',
                    fontSize: '0.9rem'
                  }}
                >
                  {currentUser && loyaltyPoints > i ? <Check size={20} /> : i + 1}
                </div>
              ))}
            </div>
            {currentUser ? (
              <p style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                You have {loyaltyPoints} point{loyaltyPoints !== 1 ? 's' : ''}!
                {loyaltyPoints >= 10 ? ' 🎉 Redeem your discount now!' : ` ${10 - loyaltyPoints} more to go!`}
              </p>
            ) : (
              <p style={{ fontSize: '1.1rem' }}>Sign up to start earning points!</p>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" style={{
        padding: '6rem 2rem',
        background: 'rgba(255, 255, 255, 0.03)',
        scrollMarginTop: '80px'
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '800',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>Why Choose Slater Bros?</h2>
          
          <p style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: 'rgba(255, 255, 255, 0.9)',
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto 3rem',
            padding: '0 1rem'
          }}>
            We are a small, family-run business offering a multi-skilled service across a wide range of areas, allowing our customers to rely on one trusted team for all their needs. Built on strong values, professionalism, and attention to detail, we pride ourselves on delivering high-quality workmanship alongside outstanding customer care. Putting our customers first is at the heart of everything we do, from the first conversation to project completion, and this commitment is what truly sets us apart. We believe in honest communication, reliable service, and building long-term relationships based on trust.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            marginTop: '3rem'
          }}>
            {[
              { title: 'Five-Star Service', desc: 'We take pride in every job, big or small' },
              { title: 'Fully Qualified', desc: 'Expert tradespeople with years of experience' },
              { title: 'Competitive Prices', desc: 'Transparent pricing with no hidden fees' },
              { title: 'Fully Insured', desc: 'Comprehensive insurance for your peace of mind' },
              { title: 'Support Local Family Business', desc: 'Keep your money in the community and support local families' },
              { title: 'Trusted Network', desc: "Can't do it? We know someone who can" }
            ].map((item, i) => (
              <div key={i} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h3 style={{ fontSize: '1.3rem', marginBottom: '0.5rem', color: '#f39c12', fontWeight: '700' }}>{item.title}</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.6' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" style={{
        padding: '6rem 2rem',
        background: 'rgba(255, 255, 255, 0.03)',
        scrollMarginTop: '80px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: '800',
              marginBottom: '1rem'
            }}>Customer Reviews</h2>
            <p style={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '2rem' }}>
              See what our customers have to say about our services
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowReviewForm(true)}
                style={{
                  padding: '1rem 2rem',
                  background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Star size={20} />
                Leave a Review
              </button>

              {!isAdmin && (
                <button
                  onClick={() => setShowAdminLogin(true)}
                  style={{
                    padding: '1rem 2rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Shield size={20} />
                  Admin Login
                </button>
              )}

              {isAdmin && (
                <div style={{
                  padding: '1rem 2rem',
                  background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Shield size={20} />
                  Admin Mode Active
                </div>
              )}
            </div>
          </div>

          {/* Review Form Modal */}
          {showReviewForm && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '1rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #2c3e50, #34495e)',
                padding: '2.5rem',
                borderRadius: '15px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>Leave a Review</h3>
                  <button
                    onClick={() => setShowReviewForm(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '1.5rem'
                    }}
                  >
                    <X size={24} />
                  </button>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Your Name *</label>
                  <input
                    type="text"
                    value={reviewName}
                    onChange={(e) => setReviewName(e.target.value)}
                    placeholder="John Smith"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Date of Work *</label>
                  <input
                    type="date"
                    value={reviewDate}
                    onChange={(e) => setReviewDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Works Carried Out *</label>
                  <input
                    type="text"
                    value={reviewWork}
                    onChange={(e) => setReviewWork(e.target.value)}
                    placeholder="e.g., Garden Maintenance, Painting"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Rating *</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star
                        key={star}
                        size={32}
                        fill={star <= reviewRating ? '#f39c12' : 'none'}
                        color={star <= reviewRating ? '#f39c12' : 'rgba(255, 255, 255, 0.3)'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setReviewRating(star)}
                      />
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Comments *</label>
                  <textarea
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    placeholder="Tell us about your experience..."
                    rows="5"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button
                  onClick={submitReview}
                  disabled={submittingReview}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: submittingReview ? 'rgba(243, 156, 18, 0.5)' : 'linear-gradient(135deg, #f39c12, #e67e22)',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    cursor: submittingReview ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </div>
          )}

          {/* Admin Login Modal */}
          {showAdminLogin && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '1rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #2c3e50, #34495e)',
                padding: '2.5rem',
                borderRadius: '15px',
                maxWidth: '400px',
                width: '100%',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>Admin Login</h3>
                  <button
                    onClick={() => setShowAdminLogin(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '1.5rem'
                    }}
                  >
                    <X size={24} />
                  </button>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Admin Password</label>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && adminLogin()}
                    placeholder="Enter admin password"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <button
                  onClick={adminLogin}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    cursor: 'pointer'
                  }}
                >
                  Login
                </button>
              </div>
            </div>
          )}

          {/* Reviews Display */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '2rem'
          }}>
            {reviews.filter(review => isAdmin || review.status === 'approved').map(review => (
              <div
                key={review.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '2rem',
                  position: 'relative'
                }}
              >
                {/* Review Status Badge */}
                {isAdmin && review.status === 'pending' && (
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: '#f39c12',
                    color: '#fff',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    PENDING
                  </div>
                )}

                {/* Star Rating */}
                <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem' }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star
                      key={star}
                      size={20}
                      fill={star <= review.rating ? '#f39c12' : 'none'}
                      color={star <= review.rating ? '#f39c12' : 'rgba(255, 255, 255, 0.3)'}
                    />
                  ))}
                </div>

                <h4 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#f39c12' }}>
                  {review.name}
                </h4>

                <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1rem' }}>
                  <p>Work Date: {new Date(review.date).toLocaleDateString()}</p>
                  <p>Service: {review.work}</p>
                </div>

                <p style={{ lineHeight: '1.6', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '1rem' }}>
                  {review.comments}
                </p>

                {/* Admin Reply */}
                {review.adminReply && (
                  <div style={{
                    background: 'rgba(243, 156, 18, 0.1)',
                    border: '1px solid rgba(243, 156, 18, 0.3)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginTop: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Shield size={16} color="#f39c12" />
                      <strong style={{ color: '#f39c12', fontSize: '0.9rem' }}>Slater Bros Projects Reply:</strong>
                    </div>
                    <p style={{ fontSize: '0.95rem', lineHeight: '1.5', color: 'rgba(255, 255, 255, 0.9)' }}>
                      {review.adminReply.text}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.5rem' }}>
                      {new Date(review.adminReply.timestamp).toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Admin Controls */}
                {isAdmin && (
                  <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {review.status === 'pending' && (
                      <button
                        onClick={() => approveReview(review.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        ✓ Approve Review
                      </button>
                    )}

                    {!review.adminReply && replyingToReview !== review.id && (
                      <button
                        onClick={() => setReplyingToReview(review.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        Reply to Review
                      </button>
                    )}

                    {replyingToReview === review.id && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <textarea
                          value={adminReplyText}
                          onChange={(e) => setAdminReplyText(e.target.value)}
                          placeholder="Write your reply..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '0.8rem',
                            borderRadius: '6px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: '#fff',
                            fontSize: '0.9rem',
                            marginBottom: '0.5rem'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => submitAdminReply(review.id)}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            Post Reply
                          </button>
                          <button
                            onClick={() => {
                              setReplyingToReview(null);
                              setAdminReplyText('');
                            }}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '6px',
                              color: '#fff',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => deleteReview(review.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(231, 76, 60, 0.2)',
                        border: '1px solid rgba(231, 76, 60, 0.5)',
                        borderRadius: '6px',
                        color: '#e74c3c',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Delete Review
                    </button>
                  </div>
                )}
              </div>
            ))}

            {reviews.filter(review => isAdmin || review.status === 'approved').length === 0 && (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '4rem 2rem',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>
                <Star size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p style={{ fontSize: '1.2rem' }}>No reviews yet. Be the first to leave a review!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{
        padding: '6rem 2rem',
        background: 'rgba(44, 62, 80, 0.5)',
        scrollMarginTop: '80px'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '800',
            marginBottom: '2rem'
          }}>Get In Touch</h2>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '1.1rem' }}>
              <Phone size={24} color="#f39c12" />
              <span>07304 064688</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '1.1rem' }}>
              <Mail size={24} color="#f39c12" />
              <span>hello@slaterbrosprojects.com</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '1.1rem' }}>
              <MapPin size={24} color="#f39c12" />
              <span>Serving Hampshire, Surrey & Berkshire</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '2rem',
        textAlign: 'center',
        background: 'rgba(44, 62, 80, 0.8)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          © 2026 Slater Bros. All rights reserved.
        </p>
        <p style={{ color: '#f39c12', fontStyle: 'italic', marginTop: '0.5rem' }}>
          Big enough to cope, small enough to care
        </p>
      </footer>

      {/* Auth Modal */}
      {showAuthModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #34495e, #4a5f7f)',
            padding: '2.5rem',
            borderRadius: '15px',
            maxWidth: '450px',
            width: '100%',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
                {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h3>
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  resetAuthForm();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '1.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={authMode === 'login' ? handleLogin : handleSignup}>
              {authMode === 'signup' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength="6"
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
              </div>

              {authMode === 'signup' && (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Phone</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.8rem',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Address</label>
                    <textarea
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required
                      rows="2"
                      style={{
                        width: '100%',
                        padding: '0.8rem',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  marginTop: '1rem'
                }}
              >
                {authMode === 'login' ? 'Login' : 'Create Account'}
              </button>

              <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.9rem' }}>
                {authMode === 'login' ? "Don't have an account? " : "Already have an account? "}
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode(authMode === 'login' ? 'signup' : 'login');
                    resetAuthForm();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#f39c12',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.9rem'
                  }}
                >
                  {authMode === 'login' ? 'Sign up' : 'Login'}
                </button>
              </p>

              <div style={{
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                marginTop: '1.5rem',
                paddingTop: '1.5rem'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAuthModal(false);
                    setShowBookingModal(true);
                    resetAuthForm();
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Continue as Guest
                </button>
                <p style={{
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginTop: '0.5rem',
                  lineHeight: '1.4'
                }}>
                  You can request a quote without creating an account
                </p>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #34495e, #4a5f7f)',
            padding: '2.5rem',
            borderRadius: '15px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>Book a Service</h3>
              <button
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedService('');
                  setBookingDate('');
                  setBookingTime('');
                  setBookingDetails('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '1.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {loyaltyPoints >= 10 && (
              <div style={{
                background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                <Award size={24} style={{ display: 'inline', marginRight: '0.5rem' }} />
                <strong>20% Loyalty Discount Available!</strong>
                <p style={{ fontSize: '0.9rem', marginTop: '0.3rem' }}>Mention this when we contact you for your quote</p>
              </div>
            )}

            <form onSubmit={handleBooking}>
              {!currentUser && (
                <>
                  <div style={{
                    background: 'rgba(243, 156, 18, 0.1)',
                    border: '1px solid rgba(243, 156, 18, 0.3)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem', color: '#f39c12', fontWeight: 'bold' }}>
                      Guest Quote Request
                    </p>
                    <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.4' }}>
                      Please provide your contact details so we can send you a quote
                    </p>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Your Name *</label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      required
                      placeholder="John Smith"
                      style={{
                        width: '100%',
                        padding: '0.8rem',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Your Email *</label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      required
                      placeholder="john@example.com"
                      style={{
                        width: '100%',
                        padding: '0.8rem',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Your Phone *</label>
                    <input
                      type="tel"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      required
                      placeholder="07123 456789"
                      style={{
                        width: '100%',
                        padding: '0.8rem',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Service</label>
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select a service</option>
                  {services.map(service => (
                    <option key={service.id} value={service.name} style={{ background: '#34495e' }}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Date</label>
                <input
                  type="date"
                  value={bookingDate}
                  onChange={(e) => setBookingDate(e.target.value)}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Time</label>
                <input
                  type="time"
                  value={bookingTime}
                  onChange={(e) => setBookingTime(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Additional Details</label>
                <textarea
                  value={bookingDetails}
                  onChange={(e) => setBookingDetails(e.target.value)}
                  rows="3"
                  placeholder="Tell us more about your job requirements..."
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{
                background: 'rgba(243, 156, 18, 0.1)',
                border: '1px solid rgba(243, 156, 18, 0.3)',
                borderRadius: '6px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{
                  fontSize: '0.75rem',
                  lineHeight: '1.5',
                  color: 'rgba(255, 255, 255, 0.8)',
                  margin: 0
                }}>
                  <strong>Please note:</strong> We will do our best to accommodate the date and time stipulated however, please note due to availability we cannot promise the works will be carried out on the date and time you've picked. We will contact you to discuss further and confirm your booking. Thank you for your understanding.
                </p>
              </div>

              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Calendar size={20} />
                Confirm Booking
              </button>
            </form>

            {userBookings.length > 0 && (
              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <h4 style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Your Bookings</h4>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {userBookings.slice().reverse().map(booking => (
                    <div key={booking.id} style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      padding: '0.8rem',
                      borderRadius: '6px',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem'
                    }}>
                      <p style={{ fontWeight: 'bold' }}>{booking.service}</p>
                      <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {new Date(booking.date).toLocaleDateString()} at {booking.time}
                      </p>
                      <p style={{ color: '#f39c12' }}>Status: {booking.status}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && pendingBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #34495e, #4a5f7f)',
            padding: '2.5rem',
            borderRadius: '15px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>Payment Details</h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setShowBookingModal(true);
                  setPendingBooking(null);
                }}
                disabled={paymentProcessing}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  cursor: paymentProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '1.5rem',
                  opacity: paymentProcessing ? 0.5 : 1
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Booking Summary */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '2rem'
            }}>
              <h4 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: '#f39c12' }}>Booking Summary</h4>
              <div style={{ fontSize: '0.95rem', lineHeight: '1.8' }}>
                <p><strong>Service:</strong> {pendingBooking.service}</p>
                <p><strong>Date:</strong> {new Date(pendingBooking.date).toLocaleDateString()}</p>
                <p><strong>Time:</strong> {pendingBooking.time}</p>
                <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.2)', marginTop: '1rem', paddingTop: '1rem' }}>
                  {pendingBooking.discount > 0 && (
                    <>
                      <p><strong>Original Price:</strong> £{pendingBooking.originalPrice}</p>
                      <p style={{ color: '#f39c12' }}><strong>Loyalty Discount (20%):</strong> -£{(pendingBooking.originalPrice * 0.2).toFixed(2)}</p>
                    </>
                  )}
                  <p style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#f39c12' }}>
                    <strong>Total:</strong> £{pendingBooking.price.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <form onSubmit={handlePayment}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Cardholder Name</label>
                <input
                  type="text"
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                  required
                  disabled={paymentProcessing}
                  placeholder="John Smith"
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Card Number</label>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\s/g, '');
                    if (value.length <= 16 && /^\d*$/.test(value)) {
                      setCardNumber(value.replace(/(\d{4})/g, '$1 ').trim());
                    }
                  }}
                  required
                  disabled={paymentProcessing}
                  placeholder="1234 5678 9012 3456"
                  maxLength="19"
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Expiry Date</label>
                  <input
                    type="text"
                    value={cardExpiry}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 4) {
                        if (value.length >= 2) {
                          setCardExpiry(value.slice(0, 2) + '/' + value.slice(2));
                        } else {
                          setCardExpiry(value);
                        }
                      }
                    }}
                    required
                    disabled={paymentProcessing}
                    placeholder="MM/YY"
                    maxLength="5"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>CVC</label>
                  <input
                    type="text"
                    value={cardCvc}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 3) {
                        setCardCvc(value);
                      }
                    }}
                    required
                    disabled={paymentProcessing}
                    placeholder="123"
                    maxLength="3"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div style={{
                background: 'rgba(243, 156, 18, 0.1)',
                border: '1px solid rgba(243, 156, 18, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem',
                fontSize: '0.85rem',
                color: 'rgba(255, 255, 255, 0.8)'
              }}>
                <strong style={{ color: '#f39c12' }}>🔒 Secure Payment</strong><br />
                Your payment information is encrypted and secure. This is a demo - use test card: 4242 4242 4242 4242
              </div>

              <button
                type="submit"
                disabled={paymentProcessing}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: paymentProcessing ? 'rgba(243, 156, 18, 0.5)' : 'linear-gradient(135deg, #f39c12, #e67e22)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  cursor: paymentProcessing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {paymentProcessing ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '3px solid #fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <Check size={20} />
                    Pay £{pendingBooking.price.toFixed(2)}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Make a Payment Modal */}
      {showPaymentTab && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
          padding: '1rem',
          overflowY: 'auto'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #2c3e50, #34495e)',
            padding: '2.5rem',
            borderRadius: '15px',
            maxWidth: '550px',
            width: '100%',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '95vh',
            overflowY: 'auto',
            margin: '2rem 0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.8rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                💳 Make a Payment
              </h3>
              <button
                onClick={() => setShowPaymentTab(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '1.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{
              background: 'rgba(39, 174, 96, 0.1)',
              border: '1px solid rgba(39, 174, 96, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '2rem'
            }}>
              <p style={{ fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>
                <strong>Request a Payment Link:</strong> Enter the amount you need to pay and your email. We'll send you a secure Stripe payment link within 24 hours where you can complete your payment safely.
              </p>
            </div>

            <form onSubmit={handleStripePayment}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600' }}>
                  Payment Amount (£) *
                </label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  required
                  min="1"
                  step="0.01"
                  placeholder="Enter amount (e.g., 50.00)"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600' }}>
                  Your Email *
                </label>
                <input
                  type="email"
                  value={paymentEmail}
                  onChange={(e) => setPaymentEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
                <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.3rem' }}>
                  We'll send your secure payment link to this email
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600' }}>
                  Booking Reference (Optional)
                </label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="e.g., Booking #12345 or Invoice #001"
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{
                background: 'rgba(243, 156, 18, 0.1)',
                border: '1px solid rgba(243, 156, 18, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ fontSize: '0.85rem', lineHeight: '1.5', margin: 0 }}>
                  📧 <strong>What happens next?</strong><br/>
                  1. We'll receive your payment request<br/>
                  2. We'll create a secure Stripe payment link<br/>
                  3. You'll receive the link via email within 24 hours<br/>
                  4. Click the link and pay securely through Stripe
                </p>
              </div>

              <button
                type="submit"
                disabled={processingPayment}
                style={{
                  width: '100%',
                  padding: '1.2rem',
                  background: processingPayment 
                    ? 'rgba(39, 174, 96, 0.5)' 
                    : 'linear-gradient(135deg, #27ae60, #2ecc71)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  cursor: processingPayment ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignments: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {processingPayment ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '3px solid #fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Submitting...
                  </>
                ) : (
                  <>
                    📧 Request Payment Link
                  </>
                )}
              </button>

              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', lineHeight: '1.4' }}>
                  🔒 Secured by Stripe • You'll receive a secure payment link via email
                </p>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Chat Messenger */}
      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f39c12, #e67e22)',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(243, 156, 18, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'transform 0.3s'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <MessageCircle size={28} />
        </button>
      )}

      {/* Chat Window */}
      {showChat && (
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          width: '380px',
          maxHeight: '600px',
          background: 'linear-gradient(135deg, #2c3e50, #34495e)',
          borderRadius: '15px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {/* Chat Header */}
          <div style={{
            background: 'linear-gradient(135deg, #f39c12, #e67e22)',
            padding: '1rem 1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <MessageCircle size={24} />
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Slater Bros Projects</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>We're here to help!</div>
              </div>
            </div>
            <button
              onClick={() => setShowChat(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              <X size={20} />
            </button>
          </div>

          {!chatStarted ? (
            /* Chat Start Form */
            <div style={{ padding: '2rem' }}>
              <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>Start a conversation</h3>
              <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                Please provide your details so we can assist you better.
              </p>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Your Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="John Smith"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Your Email</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="john@example.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              <button
                onClick={startChat}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Start Chat
              </button>

              <p style={{ 
                marginTop: '1rem', 
                fontSize: '0.75rem', 
                color: 'rgba(255, 255, 255, 0.6)',
                textAlign: 'center'
              }}>
                Response time: Up to 3 hours
              </p>
            </div>
          ) : (
            /* Chat Messages */
            <>
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                maxHeight: '400px'
              }}>
                {chatMessages.map(message => (
                  <div
                    key={message.id}
                    style={{
                      alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '80%'
                    }}
                  >
                    <div style={{
                      background: message.sender === 'user' 
                        ? 'linear-gradient(135deg, #f39c12, #e67e22)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      padding: '0.75rem 1rem',
                      borderRadius: message.sender === 'user' ? '12px 12px 0 12px' : '12px 12px 12px 0',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}>
                      {message.text}
                    </div>
                    <div style={{
                      fontSize: '0.7rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                      marginTop: '0.25rem',
                      textAlign: message.sender === 'user' ? 'right' : 'left'
                    }}>
                      {message.timestamp}
                    </div>
                  </div>
                ))}
                {sendingMessage && (
                  <div style={{ alignSelf: 'flex-start', maxWidth: '80%' }}>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      padding: '0.75rem 1rem',
                      borderRadius: '12px 12px 12px 0',
                      fontStyle: 'italic',
                      color: 'rgba(255, 255, 255, 0.6)'
                    }}>
                      Sending notification...
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div style={{
                padding: '1rem',
                background: 'rgba(0, 0, 0, 0.2)',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={handleChatKeyPress}
                    placeholder="Type your message..."
                    disabled={sendingMessage}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '0.95rem'
                    }}
                  />
                  <button
                    onClick={sendChatMessage}
                    disabled={!chatInput.trim() || sendingMessage}
                    style={{
                      padding: '0.75rem 1.25rem',
                      background: chatInput.trim() && !sendingMessage 
                        ? 'linear-gradient(135deg, #f39c12, #e67e22)' 
                        : 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      cursor: chatInput.trim() && !sendingMessage ? 'pointer' : 'not-allowed',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Send size={18} />
                  </button>
                </div>
                <p style={{ 
                  marginTop: '0.5rem', 
                  fontSize: '0.7rem', 
                  color: 'rgba(255, 255, 255, 0.5)',
                  textAlign: 'center'
                }}>
                  Response time may take up to 3 hours
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Add spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SlaterBrosLanding;